import React from 'react'
import { computeGuideGutters } from '@/lib/guides/columns-rows-fit'
import { getDevicePixelRatio } from '../helpers/device-pixel'

type ColumnsRowsOverlayProps = {
  /** Absolute live-area grid bounds in spread/canvas coordinates. */
  trimLeft: number
  trimTop: number
  trimWidth: number
  trimHeight: number
  columns?: number
  rows?: number
  colGutter?: number
  rowGutter?: number
  scale?: number
}

const ColumnsRowsOverlay: React.FC<ColumnsRowsOverlayProps> = ({
  trimLeft,
  trimTop,
  trimWidth,
  trimHeight,
  columns = 0,
  rows = 0,
  colGutter = 24,
  rowGutter = 24,
  scale = 1,
}) => {
  const innerWidth = Number.isFinite(trimWidth) ? Math.max(0, trimWidth) : 0
  const innerHeight = Number.isFinite(trimHeight) ? Math.max(0, trimHeight) : 0
  const effectiveScale = Number.isFinite(scale) && scale > 0 ? scale : 1
  const pixelsPerCanvasUnit = effectiveScale * getDevicePixelRatio()
  const hairlineThickness = 1 / pixelsPerCanvasUnit

  const lines: React.ReactNode[] = []
  const gutters: React.ReactNode[] = []
  const lineStyle = {
    position: 'absolute' as const,
    backgroundColor: 'var(--doc-column-row-guide-line)',
    opacity: 0.9,
    pointerEvents: 'none' as const,
  }
  const gutterStyle = {
    position: 'absolute' as const,
    backgroundColor: 'var(--doc-column-row-gutter-fill)',
    pointerEvents: 'none' as const,
  }

  for (const gutter of computeGuideGutters(columns, colGutter, innerWidth)) {
    gutters.push(
      <div
        key={`col-gutter-${gutter.index}`}
        data-testid="column-gutter"
        style={{
          ...gutterStyle,
          left: gutter.start,
          top: 0,
          width: Math.max(0, gutter.end - gutter.start),
          height: innerHeight,
        }}
      />
    )
    lines.push(
      <div
        key={`col-start-${gutter.index}`}
        data-testid="column-guide-line"
        style={{
          ...lineStyle,
          left: gutter.start,
          top: 0,
          width: hairlineThickness,
          height: innerHeight,
        }}
      />,
      <div
        key={`col-end-${gutter.index}`}
        data-testid="column-guide-line"
        style={{
          ...lineStyle,
          left: gutter.end,
          top: 0,
          width: hairlineThickness,
          height: innerHeight,
        }}
      />
    )
  }

  for (const gutter of computeGuideGutters(rows, rowGutter, innerHeight)) {
    gutters.push(
      <div
        key={`row-gutter-${gutter.index}`}
        data-testid="row-gutter"
        style={{
          ...gutterStyle,
          left: 0,
          top: gutter.start,
          width: innerWidth,
          height: Math.max(0, gutter.end - gutter.start),
        }}
      />
    )
    lines.push(
      <div
        key={`row-start-${gutter.index}`}
        data-testid="row-guide-line"
        style={{
          ...lineStyle,
          left: 0,
          top: gutter.start,
          width: innerWidth,
          height: hairlineThickness,
        }}
      />,
      <div
        key={`row-end-${gutter.index}`}
        data-testid="row-guide-line"
        style={{
          ...lineStyle,
          left: 0,
          top: gutter.end,
          width: innerWidth,
          height: hairlineThickness,
        }}
      />
    )
  }

  return (
    <div
      data-testid="columns-rows-overlay"
      style={{
        position: 'absolute',
        left: trimLeft,
        top: trimTop,
        width: innerWidth,
        height: innerHeight,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {gutters}
      {lines}
    </div>
  )
}

export default ColumnsRowsOverlay
